import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, InputNumber, PageHeader } from "antd";
import { CheckOutlined, CloseOutlined, MailOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import download from "downloadjs";

import {
  getLoan,
  denyLoan,
  approveLoan,
} from "../../state/actions/loanActions";
import { Container, SpaceBetween, Section, Top } from "./styles";
import FormModal from "../../globals/components/FormModal";
import { getStatusText, getStatusStyles } from "./helpers";
import { requests, getUrl } from "../../globals/requests";
const { helpers, dateUtil } = require("../../utils");
const constants = require("../../constants");

const canDenyLoanStatus = [
  constants.values.loans.status.PENDING_STATUS_NUMBER,
  constants.values.loans.status.REVIEWING_STATUS_NUMBER,
];

const MIN_AMOUNT = 50000;
const MAX_AMOUNT = 2000000;

function getErrorStatus(amount) {
  if (!amount) return "";
  if (amount < MIN_AMOUNT || amount > MAX_AMOUNT) return "error";
  return "";
}

function Loan(props) {
  const {
    loan,
    getLoan: getLoanById,
    denyLoan: denyLoanById,
    approveLoan: approveLoanById,
  } = props;
  const { loanId } = useParams();
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvedAmount, setApprovedAmount] = useState(
    loan?.financial_details?.loan_amount
      ? Number(loan?.financial_details?.loan_amount)
      : 0
  );
  const [loadingSendMail, setLoadingSendMail] = useState(false);

  const history = useHistory();

  useEffect(() => {
    async function getALoan() {
      try {
        await getLoanById(loanId);
      } catch (e) {
        helpers.displayMessage(e);
      }
    }

    getALoan(loanId);
  }, [getLoanById, loanId]);

  const monthly_repayments = useMemo(() => {
    const amountToBeRepaid = loan?.approved_amount
      ? loan?.approved_amount
      : loan?.financial_details?.loan_amount;
    const total_repayments = Math.trunc(
      Math.ceil(
        Number(amountToBeRepaid) +
          (Number(amountToBeRepaid) *
            loan.interest_rate *
            Number(loan?.financial_details?.loan_tenor)) /
            1200
      )
    );
    const repayments = Math.ceil(
      total_repayments / Number(loan?.financial_details?.loan_tenor)
    ).toFixed(2);

    return repayments;
  }, [
    loan?.interest_rate,
    loan?.financial_details?.loan_tenor,
    loan?.approved_amount,
    loan?.financial_details?.loan_amount,
  ]);

  const handleShowApproveModal = useCallback(() => {
    setApprovedAmount(Number(loan?.financial_details?.loan_amount));
    setShowApproveModal(true);
  }, [
    setApprovedAmount,
    setShowApproveModal,
    loan?.financial_details?.loan_amount,
  ]);

  const denyUserLoan = useCallback(async () => {
    try {
      await denyLoanById(loan?._id);
      setShowDenyModal(false);
      helpers.successModal("Loan Denied");
    } catch (e) {
      helpers.displayMessage(e);
    }
  }, [denyLoanById, loan?._id]);

  const resendEmailOfferNotice = useCallback(async (id) => {
    try {
      setLoadingSendMail(true);
      const url = `${getUrl("loans")}/${id}/offer`;
      await requests.getWithAuth(url);
      setLoadingSendMail(false);
      helpers.successModal({
        title: "Email Sent",
        content: "Email notice of the loan offer has been sent to the user.",
      });
    } catch (e) {
      const errMsg = helpers.getErrorMessage(e);
      helpers.infoModal({
        message: errMsg,
      });
      setLoadingSendMail(false);
    }
  }, []);

  const approveUserLoan = useCallback(async () => {
    try {
      if (!approvedAmount) {
        helpers.infoModal({
          message: "You must enter the approved amount",
        });
        return;
      }
      await approveLoanById(loan?._id, { approved_amount: approvedAmount });
      helpers.successModal({
        title: "Loan Application approved",
        content:
          "Applicant will recieve an email notifying him of the approval",
      });
      setShowApproveModal(false);
    } catch (e) {
      helpers.displayMessage(e);
    }
  }, [approveLoanById, loan?._id, approvedAmount]);

  const showResenOfferBtn = useMemo(() => {
    return (
      loan?.status === constants.values.loans.status.APPROVED_STATUS_NUMBER &&
      !loan?.loanAccepted
    );
  }, [loan.loanAccepted, loan.status]);

  return (
    <Container>
      <Top>
        <SpaceBetween>
          <PageHeader
            style={{ padding: "0px" }}
            onBack={() => history.goBack()}
            title="Loan Application"
            subTitle={getStatusText(loan?.status)}
          />
          {showResenOfferBtn && (
            <Button
              type="primary"
              icon={<MailOutlined />}
              onClick={() => resendEmailOfferNotice(loan?._id)}
              loading={loadingSendMail}
            >
              Re-Send Offer
            </Button>
          )}
        </SpaceBetween>
      </Top>
      <Section>
        <p>
          <b>Personal Details</b>
        </p>
        <SpaceBetween>
          <p>Name</p>
          <p>
            {loan?.personal_details?.first_name}&nbsp;
            {loan?.personal_details?.last_name}
          </p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Email</p>
          <p>{loan?.personal_details?.email}</p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Account Details</p>
          <p>
            {loan?.personal_details?.account_number}&nbsp;
            {loan?.personal_details?.bank_name}
          </p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Date of Birth</p>
          <p>{dateUtil.formatDate(loan?.personal_details?.date_of_birth)}</p>
        </SpaceBetween>
      </Section>

      <Section>
        <p>
          <b>Employment Details</b>
        </p>
        <SpaceBetween>
          <p>Employer</p>
          <p>{loan?.org_details?.name_of_org}</p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Staff Code</p>
          <p>{loan?.org_details?.staff_code}</p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Monthly Earnings</p>
          <p>{helpers.toCurrency(loan?.financial_details?.monthly_earning)}</p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Date of Employment</p>
          <p>
            {dateUtil.formatDate(loan?.personal_details?.date_of_employment)}
          </p>
        </SpaceBetween>
      </Section>
      <Section>
        <p>
          <b>Loan Details</b>
        </p>
        <SpaceBetween>
          <p>Loan Amount Requested</p>
          <p>{helpers.toCurrency(loan?.financial_details?.loan_amount)}</p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Approved Amount</p>
          <p>
            {loan?.approved_amount
              ? helpers.toCurrency(loan?.approved_amount)
              : "-"}
          </p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Approved On</p>
          <p>
            {loan?.approvedOn ? dateUtil.formatDate(loan?.approvedOn) : "-"}
          </p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Loan Tenor</p>
          <p>{loan?.financial_details?.loan_tenor} Months</p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Interest Rate</p>
          <p>{Math.round(loan?.interest_rate / 12).toFixed(2)}%</p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Loan Amount Requested</p>
          <p>{helpers.toCurrency(loan?.financial_details?.loan_amount)}</p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Monthly Repayments</p>
          <p>{helpers.toCurrency(monthly_repayments)}</p>
        </SpaceBetween>
        <SpaceBetween>
          <p>Due Date</p>
          <p>{loan?.dueDate ? dateUtil.formatDate(loan?.dueDate) : "-"}</p>
        </SpaceBetween>
      </Section>
      <Section>
        <p>
          <b>Documents</b>
        </p>
        <SpaceBetween>
          <p>Staff Id front</p>
          <a
            target="_blank"
            rel="noreferrer"
            href={loan?.document_details?.frontStaffIdImageUrl}
          >
            View
          </a>
        </SpaceBetween>
        <SpaceBetween>
          <p>Staff Id back</p>
          <a
            target="_blank"
            rel="noreferrer"
            href={loan?.document_details?.backStaffIdImageUrl}
          >
            View
          </a>
        </SpaceBetween>
        <SpaceBetween>
          <p>Government Id</p>
          <a
            target="_blank"
            rel="noreferrer"
            href={loan?.document_details?.governmentIdImageUrl}
          >
            View
          </a>
        </SpaceBetween>
        <SpaceBetween>
          <p>Payslip (1)</p>
          {loan?.document_details?.firstPayslipImageUrl ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={loan?.document_details?.firstPayslipImageUrl}
            >
              View
            </a>
          ) : (
            "-"
          )}
        </SpaceBetween>
        <SpaceBetween>
          <p>Payslip (2)</p>
          {loan?.document_details?.secondPayslipImageUrl ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={loan?.document_details?.secondPayslipImageUrl}
            >
              View
            </a>
          ) : (
            "-"
          )}
        </SpaceBetween>
      </Section>
      <Section>
        <SpaceBetween>
          {canDenyLoanStatus.includes(loan?.status) && (
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={() => setShowDenyModal(true)}
            >
              Deny
            </Button>
          )}
          {canDenyLoanStatus.includes(loan?.status) && (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleShowApproveModal}
            >
              Approve
            </Button>
          )}
        </SpaceBetween>
      </Section>
      <FormModal
        visible={showDenyModal}
        addTitle="Deny Application?"
        onSubmit={denyUserLoan}
        onCancel={() => setShowDenyModal(false)}
      >
        <p>Are you sure you want to deny this loan application ?</p>
      </FormModal>
      <FormModal
        visible={showApproveModal}
        addTitle="Approve Application?"
        onSubmit={approveUserLoan}
        onCancel={() => setShowApproveModal(false)}
      >
        <div>
          <p>Amount to Approve</p>
          <InputNumber
            min={50000}
            max={loan?.financial_details?.loan_amount}
            formatter={(value) =>
              `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            status={getErrorStatus(approvedAmount)}
            style={{ width: "100%" }}
            value={approvedAmount}
            parser={(value) => value.replace(/₦\s?|(,*)/g, "")}
            onChange={(value) => setApprovedAmount(value)}
          />
        </div>
      </FormModal>
    </Container>
  );
}

const mapStateToProps = (state) => {
  return {
    loan: state.loansReducer.loan,
  };
};

export default connect(mapStateToProps, { getLoan, denyLoan, approveLoan })(
  Loan
);
